<template>
  <div ref="el" class="colorpicker">
    <div
      role="application"
      aria-label="Color picker"
      class="colorpicker__palette"
    >
      <div class="colorpicker__colors" role="listbox">
        <button
          v-for="c in colorPalette"
          :key="c"
          type="button"
          role="option"
          :aria-label="`color:${c}`"
          :aria-selected="c === hexString"
          class="colorpicker__color-item"
          :class="{ 'colorpicker__color-item--white': c === '#ffffff' }"
          :style="{ background: c }"
          @click="chooseColor(c)"
        >
          <div v-if="c === 'rgba(0,0,0,0)'">
            <lucide-icon name="eye-off" />
          </div>
          <div v-show="c === hexString" class="colorpicker__dot"></div>
        </button>

        <button
          v-if="nullAllowed"
          type="button"
          role="option"
          :title="$t('actions.reset')"
          :aria-label="`color:none`"
          :aria-selected="inputColor == null"
          class="colorpicker__color-item"
          style="background-color: var(--color-background-close)"
          :style="
            inputColor == null
              ? 'outline: 2px solid var(--color-text-medium); outline-offset: -1px'
              : ''
          "
          @click="chooseColor(null)"
        >
          <lucide-icon v-if="inputColor != null" name="refresh-ccw" />
          <lucide-icon v-else name="x" style="margin-top: 1px" />
        </button>
      </div>
    </div>

    <div class="colorpicker__customcolor">
      <input
        type="color"
        class="colorpicker__input"
        :value="hexString || '#000000'"
        @input="setInputColor($event)"
      />
      <input-text
        :value="hexString || '#000000'"
        @update:value="updateInputColorHex($event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import Tinycolor from "tinycolor2";
import InputText from "./InputText.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";

interface Props {
  value?: string | null;
  transparent?: boolean;
  extraColors?: string[];
  nullAllowed?: boolean;
  confirmCustomUse?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  value: undefined,
  transparent: true,
  extraColors: () => [],
  nullAllowed: false,
  confirmCustomUse: false
});

type C<T extends boolean> = T extends true ? string | null : string;
type EmittableColor = C<typeof props.nullAllowed>;

const emit = defineEmits<{
  (e: "update:value", value: EmittableColor): void;
  (e: "select", value: EmittableColor): void;
}>();

function buildColors() {
  const colors = {
    yellows: ["#f9c432", "#fae39b", "#fff5cc", "#fffbe9"],
    oranges: ["#e87700", "#ffab5f", "#ffc599", "#ffecdc"],
    lightReds: ["#e33c42", "#eb8061", "#ffb699", "#ffe3d9"],
    reds: ["#9e2020", "#e55a5a", "#ff9999", "#ffebeb"],
    darkReds: ["#6d2121", "#a84f4d", "#b28383", "#f3d4c8"],
    greens: ["#6eb249", "#b1cc8d", "#d6e5b7", "#e7edd9"],
    darkGreens: ["#0a6645", "#40997e", "#9accbb", "#d4ebda"],
    lightBlues: ["#45b0e5", "#a4d5f4", "#cde0e8", "#e6f2f4"],
    blues: ["#0d417f", "#3e66ab", "#7a9ccc", "#c9daf2"],
    purples: ["#992e5a", "#cc527e", "#e288a8", "#efd0db"],
    violets: ["#3e1466", "#764298", "#b597cc", "#d8cfe5"],
    greys: ["#000000", "#999999", "#e0e0e0", "#ffffff"]
  };

  const colorGroups = Object.values(colors);

  const arr: string[] = [];
  const colorAmount = colorGroups.length;
  const shadeAmount = colorGroups[0].length;
  for (let i = 0; i < shadeAmount; i++) {
    for (let j = 0; j < colorAmount; j++) {
      arr.push(colorGroups[j][i]);
    }
  }
  return arr;
}
const defaultColorPalette = buildColors();

const inputColor = ref(props.value ? Tinycolor(props.value) : null);

const colorPalette = computed(() => {
  const palette = defaultColorPalette.slice();
  if (props.transparent) {
    palette.push("rgba(0,0,0,0)");
  }
  if (props.extraColors) {
    palette.push(...props.extraColors);
  }
  return palette;
});
const hexString = computed(() => {
  if (inputColor.value == null) return "";
  return inputColor.value.toHexString();
});

watch(
  () => props.value,
  (val, oldVal) => {
    if (val != oldVal) {
      updateInputColor(val || null);
    }
  },
  { immediate: true }
);

function updateInputColor(value: string | null) {
  if (value == null) {
    inputColor.value = null;
  } else if (value.startsWith("var")) {
    const varValue = getColorFromCSSVariable(value);
    inputColor.value = Tinycolor(varValue);
  } else {
    inputColor.value = Tinycolor(value);
  }
}
function updateColor(color: EmittableColor) {
  if (color) {
    const c = Tinycolor(color);
    inputColor.value = c;
    const colorAsRGBString = c.toRgbString().replaceAll(" ", "");
    emit("update:value", colorAsRGBString);
    return colorAsRGBString;
  } else {
    inputColor.value = null;
    emit("update:value", color);
    return color;
  }
}
function chooseColor(color: EmittableColor) {
  emit("select", updateColor(color));
}
function updateInputColorHex(hexString: string) {
  if (hexString.length < 7) return;

  const color = Tinycolor(hexString);
  if (color.isValid()) {
    updateColor(hexString);
  }
}
function setInputColor(event: Event) {
  const inputElement = event.target as HTMLInputElement;
  const value = inputElement.value;
  const color = Tinycolor(value);
  if (color.isValid()) {
    updateColor(value);
  }
}
function getColorFromCSSVariable(cssString: string) {
  // get color from css variable:
  const varNameMatch = cssString.match(/var\((.*)\)/);
  if (varNameMatch == null) return;
  if (varNameMatch.length < 2) return;
  const varName = varNameMatch[1];
  return getComputedStyle(document.documentElement).getPropertyValue(varName);
}
</script>

<style lang="scss">
.colorpicker {
  position: relative;

  &__palette {
    padding-top: 5px;
    padding-left: 0;
    border-radius: 2px;
    width: 246px;
    background-color: transparent;
  }

  &__colors {
    padding: 0;
    margin: 0;
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: 5px;
  }

  &__color-item {
    border: none;
    padding: 0;
    width: 15px;
    height: 15px;
    position: relative;
    cursor: pointer;
  }
  &__color-item--white {
    box-shadow: inset 0 0 0 1px #ddd;
  }
  &__color-item--white &__dot {
    background: #000;
  }

  &__dot {
    position: absolute;
    top: 5px;
    right: 5px;
    bottom: 5px;
    left: 5px;
    border-radius: 50%;
    opacity: 1;
    background: #fff;
  }

  &__input {
    appearance: none;
    -moz-appearance: none;
    -webkit-appearance: none;
    border: none;
    padding: 0;
    width: 2em;
    height: 2em;
    border-radius: 3em;
    overflow: hidden;
    flex-shrink: 0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);

    &:focus {
      outline: none;
    }
    &:focus-visible {
      outline: 3px solid var(--color-action);
      outline-offset: -1px;
    }

    &::-webkit-color-swatch-wrapper {
      padding: 0;
    }
    &::-webkit-color-swatch {
      border: 0;
      border-radius: 0;
    }
    &::-moz-color-swatch,
    &::-moz-focus-inner {
      border: 0;
    }
    &::-moz-focus-inner {
      padding: 0;
    }
  }

  &__customcolor {
    padding-top: 5px;
    padding-bottom: 5px;
    padding-right: 0px;
    position: relative;
    display: flex;
    align-items: center;
    gap: 7px;
  }

  // transparent color field:
  &__color-item[aria-label="color:rgba(0,0,0,0)"][aria-selected="true"] {
    outline: 2px solid var(--color-action);
    border-radius: 2px;
  }
  &__color-item[aria-label="color:rgba(0,0,0,0)"] &__dot {
    display: none;
    background-color: transparent;
    border-radius: 0;
    border: 1px solid var(--color-action);
  }
}
</style>
